import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from './api';
import { trackEvent } from '@/lib/analytics';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Effective permissions from the centralized RBAC system.
  // null = not resolved yet (callers must wait), [] = resolved, none granted.
  const [permissions, setPermissions] = useState(null);

  const loadPermissions = useCallback(async () => {
    // Denying on failure is the safe default, but a transient network blip
    // must not silently bounce a staff user out of their panel — so retry once.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const { data } = await api.get('/me/permissions');
        const perms = data.permissions || [];
        setPermissions(perms);
        return perms;
      } catch (e) {
        if (e?.response?.status === 401) break;
      }
    }
    setPermissions([]);
    return [];
  }, []);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem('indusfact_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      await loadPermissions();
    } catch (e) {
      localStorage.removeItem('indusfact_token');
      setUser(null);
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  }, [loadPermissions]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('indusfact_token', data.token);
    setUser(data.user);
    const perms = await loadPermissions();
    // Analytics: fire login event (skipped for staff by analytics helper).
    trackEvent('login', { method: 'password', user_role: data.user?.role });
    // `permissions` is returned so the caller can route immediately — reading
    // it from context in the same tick would still see the pre-login value.
    return { ...data.user, permissions: perms };
  };

  const register = async (payload) => {
    const { data } = await api.post('/auth/register', payload);
    localStorage.setItem('indusfact_token', data.token);
    setUser(data.user);
    await loadPermissions();
    // Analytics: sign_up is the acquisition conversion.
    trackEvent('sign_up', { method: 'password' });
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem('indusfact_token');
    setUser(null);
    setPermissions(null);
  };

  const refresh = async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      await loadPermissions();
    } catch (_) {}
  };

  // Panel/UI gating helper — ANY-of semantics, mirroring the backend's
  // permission keys. Never falls back to role strings.
  const hasAnyPermission = useCallback(
    (keys = []) => keys.some((k) => (permissions || []).includes(k)),
    [permissions],
  );

  return (
    <AuthContext.Provider value={{
      user, loading, permissions, hasAnyPermission,
      login, register, logout, refresh, setUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
